import {
  QUILL_LINK_INVALID_EVENT,
  QUILL_LINK_URL_INVALID_HINT,
  normalizeQuillLinkUrlInput,
  quillHyperlinkUrlIsAllowed,
} from "./quillHyperlinkValidation";

const LINK_URL_ERROR_CLASS = "fama-quill-link-url-error";

/**
 * Snow calls `show()` while the tooltip is visible (e.g. on SELECTION_CHANGE over a link), which
 * removes `data-mode` even during link editing — we keep this flag in sync so validation UI stays
 * consistent until the user leaves link edit or the tooltip hides.
 */
const LINK_EDIT_DATASET_KEY = "famaQuillLinkEdit" as const;

const GLOBAL_PATCH_KEY = "__famaQuillLinkTooltipPatch_v1__";

const SNOW_EXTEND_TOOLBAR_PATCH = Symbol.for(
  "__famaQuillSnowExtendToolbarPatched__"
);
const SNOW_SHOW_PATCH = Symbol.for("__famaQuillSnowTooltipShowPatched__");

function isLinkUrlTooltipState(tooltip: any): boolean {
  const root = tooltip.root as HTMLElement | undefined;
  if (!root) return false;
  if (root.getAttribute("data-mode") === "link") return true;
  return root.dataset[LINK_EDIT_DATASET_KEY] === "true";
}

function getOrCreateLinkUrlErrorEl(tooltip: any): HTMLElement | null {
  const root = tooltip.root as HTMLElement | undefined;
  if (!root) return null;
  let el = root.querySelector<HTMLElement>(`.${LINK_URL_ERROR_CLASS}`);
  if (!el) {
    el = document.createElement("div");
    el.className = LINK_URL_ERROR_CLASS;
    el.setAttribute("role", "alert");
    el.style.cssText =
      "display:none;box-sizing:border-box;width:100%;margin-top:6px;padding:0 2px;color:#c62828;font-size:12px;line-height:1.35;white-space:normal;";
    // After input + Save (Snow: a.ql-action), not between them — keeps Save on the same row as the field.
    root.appendChild(el);
  } else if (el.parentNode === root && el !== root.lastElementChild) {
    root.appendChild(el);
  }
  return el;
}

function hideLinkUrlError(tooltip: any): void {
  const el = tooltip.root?.querySelector?.(
    `.${LINK_URL_ERROR_CLASS}`
  ) as HTMLElement | null;
  if (el) {
    el.style.display = "none";
    el.textContent = "";
  }
  const tb = tooltip.textbox as HTMLInputElement | undefined;
  if (tb) {
    tb.removeAttribute("aria-invalid");
    const describedBy = tb.getAttribute("aria-describedby");
    if (describedBy && el?.id && describedBy === el.id) {
      tb.removeAttribute("aria-describedby");
    }
  }
}

function showLinkUrlError(tooltip: any): void {
  const el = getOrCreateLinkUrlErrorEl(tooltip);
  if (!el) return;
  if (!el.id) {
    el.id = `fama-quill-link-err-${Math.random().toString(36).slice(2, 11)}`;
  }
  el.textContent = QUILL_LINK_URL_INVALID_HINT;
  el.style.display = "block";
  const tb = tooltip.textbox as HTMLInputElement | undefined;
  if (tb) {
    tb.setAttribute("aria-invalid", "true");
    tb.setAttribute("aria-describedby", el.id);
  }
}

/** Snow theme: Save is `a.ql-action` (Bubble has no Save control; Enter still uses save()). */
function getLinkSaveControl(tooltip: any): HTMLElement | null {
  return tooltip.root?.querySelector?.("a.ql-action") ?? null;
}

function setLinkSaveControlEnabled(tooltip: any, enabled: boolean): void {
  const save = getLinkSaveControl(tooltip);
  if (!save) return;
  if (enabled) {
    save.removeAttribute("aria-disabled");
    save.style.pointerEvents = "";
    save.style.opacity = "";
    save.style.cursor = "";
    save.classList.remove("fama-quill-link-save-disabled");
  } else {
    save.setAttribute("aria-disabled", "true");
    save.style.pointerEvents = "none";
    save.style.opacity = "0.45";
    save.style.cursor = "not-allowed";
    save.classList.add("fama-quill-link-save-disabled");
  }
}

/** Snow link tooltip: bind live validation on the URL field (works with a minimal `{ root, textbox }`). */
function attachLinkTooltipInputListeners(tooltip: any): void {
  const tb = tooltip.textbox as HTMLInputElement | undefined;
  if (!tb || tb.dataset.famaQuillLinkErrorInputBound === "1") return;
  tb.dataset.famaQuillLinkErrorInputBound = "1";
  tb.addEventListener("input", () => {
    syncLinkUrlFieldUi(tooltip);
  });
  tb.addEventListener("keyup", () => {
    syncLinkUrlFieldUi(tooltip);
  });
  tb.addEventListener("compositionend", () => {
    syncLinkUrlFieldUi(tooltip);
  });
  tb.addEventListener("paste", () => {
    queueMicrotask(() => {
      syncLinkUrlFieldUi(tooltip);
    });
  });
  tb.addEventListener("cut", () => {
    queueMicrotask(() => {
      syncLinkUrlFieldUi(tooltip);
    });
  });
}

/**
 * Tooltips constructed before `installQuillLinkTooltipSaveValidation()` ran never got
 * `listen()` from the patched prototype — attach URL-field listeners to those DOM nodes.
 */
function scanAndAttachLinkTooltipInputs(): void {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll<HTMLInputElement>(".ql-tooltip input[type='text']")
    .forEach((input) => {
      const root = input.closest(".ql-tooltip");
      if (!root?.querySelector("a.ql-preview")) return;
      if (input.dataset.famaQuillLinkErrorInputBound === "1") return;
      attachLinkTooltipInputListeners({ root, textbox: input });
    });
}

/** Live validation: error text + Save enabled only when empty (clear link) or http(s) valid. */
function syncLinkUrlFieldUi(tooltip: any): void {
  if (!isLinkUrlTooltipState(tooltip)) {
    setLinkSaveControlEnabled(tooltip, true);
    hideLinkUrlError(tooltip);
    return;
  }
  const raw = tooltip.textbox?.value ?? "";
  const normalized = normalizeQuillLinkUrlInput(raw);
  const allowed = normalized === "" || quillHyperlinkUrlIsAllowed(normalized);

  setLinkSaveControlEnabled(tooltip, allowed);

  if (normalized === "") {
    hideLinkUrlError(tooltip);
  } else if (!quillHyperlinkUrlIsAllowed(normalized)) {
    showLinkUrlError(tooltip);
  } else {
    hideLinkUrlError(tooltip);
  }
}

const USER = "user";

/** On the shared Quill prototype so only one patch applies even if this module loads more than once. */
const BASE_TOOLTIP_PATCH_MARKER = Symbol.for(
  "__famaQuillLinkTooltipPatch_applied__"
);

let installed = false;
let installSkipped = false;

/**
 * Snow's `show()` calls `super.show()`, which removes `ql-editing`. That hides the URL
 * `<input>` (Snow CSS) while the user may still be focused on it after a failed save — so
 * `input` events stop and validation UI never updates. Restore editing chrome when the link
 * field still has focus and we are in a link edit session.
 */
function patchSnowTooltipShowOnce(TooltipConstructor: {
  prototype: { show: (...args: unknown[]) => unknown };
}): void {
  const proto = TooltipConstructor.prototype as Record<PropertyKey, unknown>;
  if (proto[SNOW_SHOW_PATCH]) return;
  const originalShow = TooltipConstructor.prototype.show;
  if (typeof originalShow !== "function") return;

  TooltipConstructor.prototype.show = function famaSnowTooltipShow(this: any) {
    const preserveEditingUi =
      this.root?.dataset?.[LINK_EDIT_DATASET_KEY] === "true" &&
      this.textbox &&
      document.activeElement === this.textbox;
    const result = originalShow.call(this);
    if (preserveEditingUi) {
      this.root?.classList?.add("ql-editing");
      if (this.root?.getAttribute?.("data-mode") == null) {
        this.root.setAttribute("data-mode", "link");
      }
      queueMicrotask(() => {
        this.textbox?.focus?.();
        syncLinkUrlFieldUi(this);
      });
    }
    return result;
  };
  proto[SNOW_SHOW_PATCH] = true;
}

function patchSnowThemeExtendToolbarOnce(SnowTheme: {
  prototype: { extendToolbar: (...args: unknown[]) => unknown };
}): void {
  const proto = SnowTheme.prototype as Record<PropertyKey, unknown>;
  if (proto[SNOW_EXTEND_TOOLBAR_PATCH]) return;
  const originalExtend = SnowTheme.prototype.extendToolbar;
  if (typeof originalExtend !== "function") return;

  SnowTheme.prototype.extendToolbar = function famaSnowExtendToolbar(
    this: any,
    toolbar: unknown
  ) {
    const ret = originalExtend.call(this, toolbar);
    const tooltip = this.tooltip;
    if (tooltip?.preview && tooltip.constructor?.prototype?.show) {
      patchSnowTooltipShowOnce(tooltip.constructor);
    }
    return ret;
  };
  proto[SNOW_EXTEND_TOOLBAR_PATCH] = true;
}

function patchTooltipHideToClearLinkEditFlag(TooltipClass: {
  prototype: { hide: (...args: unknown[]) => unknown };
}): void {
  const proto = TooltipClass.prototype as Record<PropertyKey, unknown>;
  const HIDE_PATCH = Symbol.for("__famaQuillTooltipHidePatched__");
  if (proto[HIDE_PATCH]) return;
  const originalHide = TooltipClass.prototype.hide;
  TooltipClass.prototype.hide = function famaTooltipHide(this: any) {
    const root = this.root as HTMLElement | undefined;
    if (root?.dataset) delete root.dataset[LINK_EDIT_DATASET_KEY];
    return originalHide.call(this);
  };
  proto[HIDE_PATCH] = true;
}

/**
 * Idempotent: ensures prototype patch is applied, then binds URL-field listeners on any
 * Snow tooltip inputs already in the document (e.g. created before the patch ran).
 */
export function upgradeExistingLinkTooltipInputs(): void {
  installQuillLinkTooltipSaveValidation();
  scanAndAttachLinkTooltipInputs();
}

/**
 * Patches Quill BaseTooltip.prototype.save once. The Snow link popup (toolbar control with
 * aria-label="link" → URL field → Enter or apply) calls this save().
 */
export function installQuillLinkTooltipSaveValidation(): void {
  if (installed || installSkipped) return;

  const g = globalThis as Record<string, unknown>;
  if (g[GLOBAL_PATCH_KEY]) {
    installed = true;
    return;
  }

  let BaseTooltip: {
    prototype: {
      edit: (...args: any[]) => unknown;
      listen: (...args: any[]) => unknown;
      save: (...args: any[]) => unknown;
    };
  };
  let TooltipClass: { prototype: { hide: (...args: unknown[]) => unknown } };
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports -- Webpack provides CJS; Jest cannot load quill ESM.
    BaseTooltip = require("quill/themes/base.js").BaseTooltip;
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    TooltipClass = require("quill/ui/tooltip.js").default;
  } catch {
    installSkipped = true;
    return;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const snowMod = require("quill/themes/snow.js");
    const SnowTheme = (snowMod.default ?? snowMod) as {
      prototype: { extendToolbar: (...args: unknown[]) => unknown };
    };
    patchSnowThemeExtendToolbarOnce(SnowTheme);
  } catch {
    /* Bubble-only or test env without snow — BaseTooltip patch still applies */
  }

  const proto = BaseTooltip.prototype as Record<PropertyKey, unknown>;
  if (proto[BASE_TOOLTIP_PATCH_MARKER]) {
    installed = true;
    g[GLOBAL_PATCH_KEY] = true;
    return;
  }

  installed = true;

  patchTooltipHideToClearLinkEditFlag(TooltipClass);

  const originalListen = BaseTooltip.prototype.listen;
  BaseTooltip.prototype.listen = function listenWithLinkErrorDismiss(
    this: any
  ) {
    originalListen.call(this);
    attachLinkTooltipInputListeners(this);
  };

  const originalEdit = BaseTooltip.prototype.edit;
  BaseTooltip.prototype.edit = function editWithLinkFieldSync(this: any) {
    const mode =
      arguments.length > 0 && arguments[0] !== undefined
        ? arguments[0]
        : "link";
    const result = originalEdit.apply(this, arguments as unknown as []);
    const root = this.root as HTMLElement | undefined;
    if (mode === "link") {
      if (root?.dataset) root.dataset[LINK_EDIT_DATASET_KEY] = "true";
      queueMicrotask(() => {
        syncLinkUrlFieldUi(this);
      });
    } else {
      if (root?.dataset) delete root.dataset[LINK_EDIT_DATASET_KEY];
      setLinkSaveControlEnabled(this, true);
      hideLinkUrlError(this);
    }
    return result;
  };

  const originalSave = BaseTooltip.prototype.save;

  BaseTooltip.prototype.save = function saveWithLinkUrlValidation(this: any) {
    const rootEl = this.root as HTMLElement | undefined;
    if (
      rootEl?.dataset?.[LINK_EDIT_DATASET_KEY] === "true" &&
      rootEl.getAttribute("data-mode") == null
    ) {
      rootEl.setAttribute("data-mode", "link");
    }

    if (this.root?.getAttribute("data-mode") === "link") {
      const raw = this.textbox?.value ?? "";
      const normalized = normalizeQuillLinkUrlInput(raw);
      const quill = this.quill;

      if (normalized === "") {
        hideLinkUrlError(this);
        const scrollTop = quill.root.scrollTop;
        if (this.linkRange) {
          quill.formatText(this.linkRange, "link", false, USER);
          delete this.linkRange;
        } else {
          this.restoreFocus?.();
          quill.format("link", false, USER);
        }
        quill.root.scrollTop = scrollTop;
        if (this.textbox) this.textbox.value = "";
        this.hide?.();
        return;
      }

      if (!quillHyperlinkUrlIsAllowed(normalized)) {
        showLinkUrlError(this);
        quill.root.dispatchEvent(
          new CustomEvent(QUILL_LINK_INVALID_EVENT, {
            bubbles: false,
            detail: { url: normalized },
          })
        );
        this.textbox?.focus();
        this.textbox?.select();
        return;
      }

      hideLinkUrlError(this);
      if (this.textbox) this.textbox.value = normalized;
    }

    return originalSave.call(this);
  };

  proto[BASE_TOOLTIP_PATCH_MARKER] = true;
  g[GLOBAL_PATCH_KEY] = true;

  scanAndAttachLinkTooltipInputs();
}
